import { boxCenter, layerBox, rotate, snapAngle } from '../../../model/geometry';
import { patchLayer } from '../../../model/operations';
import { LIMITS, clamp } from '../../../model/schema';

// Pointer transforms in document pixels. Each takes the layer as it was when the gesture
// started and the pointer now, and returns the layer as it would be: the stage paints that
// draft every frame and commits it once, on release, as a single undo step.
const MIN = 8;
const round = value => Math.round(value * 100) / 100;

// Which handles a layer offers: text resizes its width (sides) or scales (corners); icons
// keep their square; a line only stretches along itself.
export function handlesFor(layer) {
  if (layer.type === 'text') return ['nw', 'ne', 'se', 'sw', 'e', 'w'];
  if (layer.type === 'icon') return ['nw', 'ne', 'se', 'sw'];
  if (layer.type === 'shape' && layer.shape === 'line') return ['e', 'w'];
  return ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
}

const HANDLE_ANGLE = { n: 0, ne: 45, e: 90, se: 135, s: 180, sw: 225, w: 270, nw: 315 };
const CURSORS = ['ns-resize', 'nesw-resize', 'ew-resize', 'nwse-resize'];
export const handleCursor = (handle, rotation) => CURSORS[Math.round((((HANDLE_ANGLE[handle] + rotation) % 180) + 180) % 180 / 45) % 4];

export function moveLayer(origin, start, point) {
  return { ...origin, x: round(origin.x + point.x - start.x), y: round(origin.y + point.y - start.y) };
}

export function resizeLayer(origin, handle, point, { free = false } = {}) {
  const box = layerBox(origin);
  const sx = handle.includes('e') ? 1 : handle.includes('w') ? -1 : 0;
  const sy = handle.includes('s') ? 1 : handle.includes('n') ? -1 : 0;
  const center = boxCenter(box);
  const local = rotate(point, center, -box.rotation);
  const lx = local.x - center.x;
  const ly = local.y - center.y;
  const ax = (-sx * box.width) / 2;
  const ay = (-sy * box.height) / 2;
  let width = sx ? Math.max(MIN, (lx - ax) * sx) : box.width;
  let height = sy ? Math.max(MIN, (ly - ay) * sy) : box.height;
  const corner = sx !== 0 && sy !== 0;
  const text = origin.type === 'text';
  // Images keep their proportions from a corner unless Shift frees them; shapes are free
  // unless Shift locks them; text and icons always scale.
  const keepRatio = corner && (text || origin.type === 'icon' || (origin.type === 'image' ? !free : free));
  if (keepRatio) {
    const factor = Math.max(width / box.width, height / box.height);
    width = box.width * factor;
    height = box.height * factor;
  }
  let next;
  if (text && corner) {
    const factor = width / box.width;
    const style = { font_size: round(clamp(origin.style.font_size * factor, LIMITS.fontSize)) };
    if (origin.style.highlight) style.highlight = { ...origin.style.highlight, padding: round(origin.style.highlight.padding * factor), radius: round(origin.style.highlight.radius * factor) };
    next = patchLayer(origin, { width: round(box.width * factor), style });
  } else if (text) next = patchLayer(origin, { width: round(Math.max(24, width)) });
  else next = { ...origin, width: round(width), height: round(height) };
  const w = next.width;
  const h = text ? layerBox(next).height : next.height;
  // The opposite edge or corner stays where it was; a text box keeps its top when its
  // width changes and its lines reflow.
  const cx = sx ? ax + (sx * w) / 2 : 0;
  const cy = sy ? ay + (sy * h) / 2 : text ? -box.height / 2 + h / 2 : 0;
  const world = rotate({ x: center.x + cx, y: center.y + cy }, center, box.rotation);
  return { ...next, x: round(world.x - w / 2), y: round(world.y - h / 2) };
}

export function rotateLayer(origin, point, { step = false } = {}) {
  const center = boxCenter(layerBox(origin));
  const angle = (Math.atan2(point.y - center.y, point.x - center.x) * 180) / Math.PI + 90;
  return { ...origin, rotation: snapAngle(angle, step ? 15 : 0) };
}

// The fields a gesture changed, as the patch committed to the document.
export function gesturePatch(origin, next) {
  const patch = {};
  ['x', 'y', 'width', 'height', 'rotation'].forEach(key => { if (next[key] !== origin[key]) patch[key] = next[key]; });
  if (next.style && next.style !== origin.style) patch.style = next.style;
  return patch;
}
